<template>
    <div class="min-height">
        <div class="card">
            <div class="card-header">
                <p class="hint mb-1">Order Id</p>
                <h4 class="card-title mt-0 mb-2">{{ order.id }}</h4>
                <payment-status-badge :currentPaymentStatus="order[ORDERS.fields.currentPaymentStatus]"> </payment-status-badge>
                <order-status-badge :orderStatus="order[ORDERS.fields.orderStep][order[ORDERS.fields.lastOrderStepActionId]][ORDERS.orderStep.action]">
                </order-status-badge>
            </div>
            <div class="card-body d-flex flex-column">
                <div class="row my-3">
                    <div class="col-xl-6">
                        <p class="hint mb-0">Placed By</p>
                        <h6>{{ order[ORDERS.fields.name] }}</h6>
                        <h6>{{ order.email }}</h6>
                    </div>
                    <div class="col-xl-6">
                        <p class="hint mb-0">Placed On</p>
                        <h6>{{ addedDate }}</h6>
                    </div>
                    <div class="col-xl-12 mt-3">
                        <p class="hint mb-1">Deliver To</p>
                        <p class="mb-1">{{ order[ORDERS.fields.deliveryAddress][ORDERS.deliveryAddress.address1] }}</p>
                        <p v-if="order[ORDERS.fields.deliveryAddress][ORDERS.deliveryAddress.address2]" class="mb-1">
                            {{ order[ORDERS.fields.deliveryAddress][ORDERS.deliveryAddress.address2] }}
                        </p>
                        <p class="mb-1">
                            {{
                                `${order[ORDERS.fields.deliveryAddress][ORDERS.deliveryAddress.city]} ${
                                    order[ORDERS.fields.deliveryAddress][ORDERS.deliveryAddress.province]
                                } ${order[ORDERS.fields.deliveryAddress][ORDERS.deliveryAddress.postalCode]}`
                            }}
                        </p>
                        <p>{{ order[ORDERS.fields.deliveryAddress][ORDERS.deliveryAddress.country] }}</p>
                    </div>
                </div>
            </div>
            <div class="card-footer">
                <div class="row">
                    <div class="col-xl-12">
                        <p v-if="error" class="text-danger">{{ error }}</p>
                        <p-button v-if="requiresResubmit" :disabled="loading" type="secondary" size="sm" @click="resubmit">
                            <i v-if="loading" class="fa fa-circle-o-notch fa-spin"></i>
                            {{ loading ? null : 'Resubmit' }}
                        </p-button>
                        <p-button v-else-if="requiresRestore" :disabled="loading" type="secondary" size="sm" @click="restore">
                            <i v-if="loading" class="fa fa-circle-o-notch fa-spin"></i>
                            {{ loading ? null : 'Restore' }}
                        </p-button>
                        <p-button
                            v-if="
                                order[ORDERS.fields.orderStep][order[ORDERS.fields.lastOrderStepActionId]][ORDERS.orderStep.action] !== ORDER_ACTIONS.CANCELED
                            "
                            :disabled="
                                loading ||
                                    order[ORDERS.fields.orderStep][order[ORDERS.fields.lastOrderStepActionId]][ORDERS.orderStep.action] ===
                                        ORDER_ACTIONS.CANCELED
                            "
                            type="danger"
                            size="sm"
                            @click="cancelOrder"
                        >
                            <i v-if="loading" class="fa fa-circle-o-notch fa-spin"></i>
                            {{ loading ? null : 'Cancel Order' }}
                        </p-button>
                    </div>
                    <div class="col-xl-12 mt-2">
                        <div v-if="requiresResubmit" class="mt-2">
                            <p class="text-danger">This order does not have an associated transaction and requires resubmission.</p>
                            <p class="text-danger">Please check order messages for errors to ensure resubmission is valid.</p>
                        </div>
                        <p v-if="requiresRestore" class="text-danger">
                            This order failed to authorize. Restoring the order will allow the user to select a different payment method and resubmit the order.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import swal from 'sweetalert2'
import { ORDER_ACTIONS, PAYMENT_STATUSES } from '@/utils/constants'
import { USERS, ORDERS } from 'db'
import PaymentStatusBadge from 'Shared/Badges/PaymentStatusBadge'
import OrderStatusBadge from 'Shared/Badges/OrderStatusBadge'
export default {
    components: {
        OrderStatusBadge,
        PaymentStatusBadge
    },
    props: {
        order: {
            type: [Object, Boolean],
            required: true
        }
    },
    data() {
        return {
            ORDER_ACTIONS,
            PAYMENT_STATUSES,
            USERS,
            ORDERS,
            loading: false,
            error: false
        }
    },
    computed: {
        addedDate() {
            return this.order[ORDERS.fields.createdAt]
                ? new Date(this.order[ORDERS.fields.createdAt]).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric'
                  })
                : 'N/A'
        },
        requiresResubmit() {
            return !this.order[ORDERS.fields.referenceTransactionId] && this.order[ORDERS.fields.currentPaymentStatus] === PAYMENT_STATUSES.PAYMENT_DELAYED
        },
        requiresRestore() {
            return this.order[ORDERS.fields.referenceTransactionId] && this.order[ORDERS.fields.currentPaymentStatus] === PAYMENT_STATUSES.PAYMENT_DELAYED
        }
    },
    methods: {
        async cancelOrder() {
            try {
                await swal({
                    title: `Are you sure you want to cancel this order?`,
                    text: 'Once cancelled, the order cannot be reopened.',
                    type: 'question',
                    showCancelButton: true,
                    confirmButtonClass: 'btn btn-danger btn-fill',
                    cancelButtonClass: 'btn btn-light btn-fill',
                    confirmButtonText: 'Confirm Cancel Order',
                    buttonsStyling: false
                })
                this.loading = true
                this.error = false
                await this.$store.dispatch('orders/CANCEL_ORDER', {
                    orderId: this.order.id,
                    action: ORDER_ACTIONS.CANCELED,
                    currentActionId: this.order[ORDERS.fields.lastOrderStepActionId]
                })
                this.$notify({
                    icon: 'nc-icon nc-check-2',
                    message: 'The order was successfully canceled',
                    type: 'success',
                    horizontalAlign: 'center',
                    verticalAlign: 'bottom',
                    timeout: 2000
                })
            } catch (err) {
                this.error = err.message
            } finally {
                this.loading = false
            }
        },
        async resubmit() {
            try {
                this.loading = true
                this.error = false
                await swal({
                    title: `Are you sure you want to resubmit this order?`,
                    type: 'question',
                    showCancelButton: true,
                    confirmButtonClass: 'btn btn-primary btn-fill',
                    cancelButtonClass: 'btn btn-light btn-fill',
                    confirmButtonText: 'Confirm Resubmit',
                    buttonsStyling: false
                })
                await this.$store.dispatch('orders/RESUBMIT_ORDER', {
                    orderId: this.order.id
                })
                this.$notify({
                    icon: 'nc-icon nc-check-2',
                    message: 'The order was successfully resubmitted',
                    type: 'success',
                    horizontalAlign: 'center',
                    verticalAlign: 'bottom',
                    timeout: 2000
                })
            } catch (err) {
                this.error = err.message
            } finally {
                this.loading = false
            }
        },
        async restore() {
            try {
                this.loading = true
                this.error = false
                await swal({
                    title: `Are you sure you want to restore this order?`,
                    type: 'question',
                    showCancelButton: true,
                    confirmButtonClass: 'btn btn-primary btn-fill',
                    cancelButtonClass: 'btn btn-light btn-fill',
                    confirmButtonText: 'Confirm Restore',
                    buttonsStyling: false
                })
                await this.$store.dispatch('orders/RESTORE_ORDER', {
                    orderId: this.order.id
                })
                this.$notify({
                    icon: 'nc-icon nc-check-2',
                    message: 'The order was successfully restored',
                    type: 'success',
                    horizontalAlign: 'center',
                    verticalAlign: 'bottom',
                    timeout: 2000
                })
            } catch (err) {
                this.error = err.message
            } finally {
                this.loading = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.card {
    height: 100%;
}
.p-badge {
    margin-right: 0.25rem;
}
th {
    width: 300px;
}
</style>
